// context/CartContext.tsx
'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { useCart as useShopifyCart } from '@/hooks/useCart';
import { Cart } from '@/types/shopify-cart';

// Updated CartItem type to include handle
export interface CartItem {
  id: string;
  productId: string;
  variantId: string;
  title: string;
  variantTitle: string;
  price: number;
  quantity: number;
  image: string;
  handle?: string; // Added handle field
}

// Cart attributes type definition
export interface CartAttributes {
  [key: string]: string;
}

// Context type definition
interface CartContextType {
  items: CartItem[];
  cart?: Cart | null;
  addItem: (item: Omit<CartItem, 'id'>) => Promise<void>;
  removeItem: (id: string) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  totalItems: number;
  totalPrice: number;
  isOpen: boolean;
  toggleCart: () => void;
  isLoading?: boolean;
  cartAttributes?: CartAttributes;
  updateCartAttributes: (attributes: CartAttributes) => Promise<void>;
  cartNote?: string;
  updateCartNote: (note: string) => Promise<void>;
  fetchCart: () => Promise<void>;
}

// Default values
const CartContext = createContext<CartContextType>({
  items: [],
  addItem: async () => {},
  removeItem: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  itemCount: 0,
  totalItems: 0,
  totalPrice: 0,
  isOpen: false,
  toggleCart: () => {},
  isLoading: false,
  cartAttributes: {},
  updateCartAttributes: async () => {},
  cartNote: '',
  updateCartNote: async () => {},
  fetchCart: async () => {},
});

// Provider component
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const shopifyCart = useShopifyCart();

  // Legacy addItem function wrapper
  const addItem = async (newItem: Omit<CartItem, 'id'>) => {
    await shopifyCart.addItem({
      variantId: newItem.variantId,
      quantity: newItem.quantity,
    });
  };

  // LineId-based updateQuantity and removeItem functions
  const updateQuantity = async (lineId: string, quantity: number) => {
    return await shopifyCart.updateQuantity(lineId, quantity);
  };

  const removeItem = (lineId: string) => {
    shopifyCart.removeItem(lineId);
  };

  // Cart attributes update function
  const updateCartAttributes = async (attributes: CartAttributes) => {
    if (!shopifyCart.updateCartAttributes) {
      console.error('updateCartAttributes is not available in shopifyCart');
      return;
    }
    await shopifyCart.updateCartAttributes(attributes);
  };

  // Cart note update function
  const updateCartNote = async (note: string) => {
    if (!shopifyCart.updateCartNote) {
      console.error('updateCartNote is not available in shopifyCart');
      return;
    }
    await shopifyCart.updateCartNote(note);
  };

  return (
    <CartContext.Provider
      value={{
        items: shopifyCart.items,
        cart: shopifyCart.cart,
        addItem,
        removeItem,
        removeFromCart: removeItem,
        updateQuantity,
        clearCart: shopifyCart.clearCart,
        itemCount: shopifyCart.itemCount,
        totalItems: shopifyCart.totalItems,
        totalPrice: shopifyCart.totalPrice,
        isOpen: shopifyCart.isOpen,
        toggleCart: shopifyCart.toggleCart,
        isLoading: shopifyCart.isLoading,
        cartAttributes: shopifyCart.cartAttributes,
        updateCartAttributes,
        cartNote: shopifyCart.cartNote,
        updateCartNote,
        fetchCart: shopifyCart.fetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Custom hook
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};